import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useI18n, formatDate } from '../i18n';
import { getUnreadCount, getNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification } from '../api/client';
import { useSocket, bindSocket } from '../hooks/useSocket';
import { useBusy, useBusyKeys } from '../hooks/useBusy';
import { useToast } from '../hooks/useToast';
import { usePushDisableMessage, usePushEnableMessage, usePushSubscription } from '../hooks/usePushSubscription';
import { renderNotification } from '../lib/notificationText';
import { groupNotifications } from '../lib/notificationGroups';
import { BellIcon, XMarkIcon, ChevronDownIcon, ChevronRightIcon } from './shared/Icons';
import { CORNER_TOKEN, renderWithIcons } from './shared/InlineIcons';
import { record, logBackgroundError } from '../observability';
import { errorCode } from '../lib/httpError';
import { onResume } from '../lib/resume';
import type { Notification } from '../types';

// The header bell: unread badge, the dropdown, the push toggle.
//
// Delivery: the backend sends `notification:new` to the user's socket room
// on every insert (services/notifications.ts), so the badge moves at once
// and an open dropdown gets the row prepended. The unread-count poll stays
// as a fallback for a tab whose socket is down, at 5 minutes instead of 30 s.
// Only rendered for a signed-in user, so the socket this opens is theirs.

const POLL_MS = 5 * 60 * 1000;
// The time of a notice has no year: the server deletes a notice after 30 days at most (cleanupExpiredNotifications).
const NOTICE_TIME = 'dd MMM HH:mm';

export default function NotificationBell() {
  const user = useAuthStore(s => s.user);
  const { t } = useI18n();
  const navigate = useNavigate();
  const socket = useSocket();
  const push = usePushSubscription();
  const explainEnable = usePushEnableMessage();
  // #557 review — a failed Disable shows its reason, as a failed Enable does.
  const explainDisable = usePushDisableMessage();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Notification[]>([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  /** #495 — the groups that the user opened, by their key (type | tournament). */
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);
  openRef.current = open;
  // #605 — the rows of the last render. An answer reads the current state
  // here, so the count goes down only for a row that is still unread.
  const rowsRef = useRef<Notification[]>([]);
  rowsRef.current = rows;
  // #605 — a lock for "Mark all read", and a lock for each row.
  const markAllBusy = useBusy();
  const marks = useBusyKeys();
  const dismissals = useBusyKeys();
  const { showError } = useToast();

  // Poll fallback. Gated on navigator.onLine so airplane-mode sessions do not
  // generate guaranteed-fail dependency entries in App Insights.
  useEffect(() => {
    if (!user) { setCount(0); return; }
    const load = () => {
      if (!navigator.onLine) return;
      getUnreadCount().then(r => setCount(r.data.unread_count)).catch(logBackgroundError('Layout:unread-count-poll'));
    };
    load();
    const interval = setInterval(load, POLL_MS);
    const onOnline = () => load();
    window.addEventListener('online', onOnline);
    // #499 — a notification of a socket gap is lost; read the count again.
    const offResume = onResume(load);
    return () => { clearInterval(interval); window.removeEventListener('online', onOnline); offResume(); };
  }, [user?.id]);

  // Live delivery. `bindSocket()` takes the listener off with the same reference.
  useEffect(() => {
    if (!socket) return;
    const onNew = (n: Notification) => {
      setCount(c => c + 1);
      if (openRef.current) setRows(prev => [n, ...prev.filter(x => x.id !== n.id)]);
    };
    return bindSocket(socket, { 'notification:new': onNew });
  }, [socket]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      try { const r = await getNotifications(); setRows(r.data.notifications); setCount(r.data.unread_count); }
      catch (err) { logBackgroundError('Layout:open-notifications')(err); }
    }
  }

  /** #605 — show these rows as read. The count goes down only for the rows
   *  that are unread in the current state, so each row counts one time. */
  function showRead(ids: ReadonlySet<string>) {
    const unread = rowsRef.current.filter(x => ids.has(x.id) && !x.read).length;
    rowsRef.current = rowsRef.current.map(x => ids.has(x.id) ? { ...x, read: true } : x);
    setRows(prev => prev.map(x => ids.has(x.id) ? { ...x, read: true } : x));
    if (unread > 0) setCount(c => Math.max(0, c - unread));
  }

  function markAll() {
    return markAllBusy.run(async () => {
      // #605 — only the rows that are unread at the click. A notice that
      // comes during the request stays unread, and the count keeps it.
      const ids = new Set(rowsRef.current.filter(n => !n.read).map(n => n.id));
      try {
        await markAllNotificationsRead();
      } catch (err) { showError(err); return; }   // #605 — a network error showed nothing
      showRead(ids);
    });
  }

  function onRowClick(n: Notification) {
    record({ event: 'notification_clicked', props: { type: n.type || '' } });
    // #605 — navigate first. A slow or failed mark kept the person on the page.
    if (n.link) { navigate(n.link); setOpen(false); }
    if (n.read) return;
    // The mark runs after the navigation, one request for each row at a time.
    // A failed mark shows its error (logic-reviewer §15), and the row stays
    // unread, as the server keeps it.
    void marks.run(n.id, () => markNotificationRead(n.id)
      .then(() => showRead(new Set([n.id])))
      .catch(showError));
  }

  function dismiss(n: Notification) {
    // #605 — the row stays dim, with its X disabled, until the answer. Then it
    // goes. Before, it went at once, and the second click of a double click
    // hit the X of the next row.
    return dismissals.run(n.id, async () => {
      try {
        await deleteNotification(n.id);
      } catch (err) {
        // NOTIFICATION_NOT_FOUND: another tab removed the row. It goes here too.
        const code = errorCode(err);
        if (code !== 'NOTIFICATION_NOT_FOUND') { showError(err); return; }
      }
      const unread = rowsRef.current.some(x => x.id === n.id && !x.read);
      rowsRef.current = rowsRef.current.filter(x => x.id !== n.id);
      setRows(prev => prev.filter(x => x.id !== n.id));
      if (unread) setCount(c => Math.max(0, c - 1));
    });
  }

  if (!user) return null;
  const visible = unreadOnly ? rows.filter(n => !n.read) : rows;
  const entries = groupNotifications(visible);
  const toggleGroup = (key: string) => setOpenGroups(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // #603 — the corner dot of a bout is an SVG: it gets the name of the corner.
  const iconNames = { [CORNER_TOKEN.red]: t.common.red, [CORNER_TOKEN.blue]: t.common.blue };
  const row = (n: Notification, nested = false) => {
    const text = renderNotification(n, t);
    // #605 — the row is dim while its Dismiss runs.
    const leaving = dismissals.isBusy(n.id);
    return (
      <div key={n.id} data-testid={nested ? 'notification-group-item' : undefined} aria-busy={leaving}
        className={`flex items-start border-b border-gray-50 hover:bg-gray-50 transition-colors ${nested ? 'pl-4 bg-gray-50/50' : ''} ${leaving ? 'opacity-40' : n.read ? 'opacity-60' : ''}`}>
        <button onClick={() => onRowClick(n)} className="flex-1 min-w-0 text-left px-4 py-3">
          {/* #603 — a token of the text (the corner dot of a bout) draws the SVG of the app. */}
          <div className="text-sm font-medium text-gray-800">{renderWithIcons(text.title, iconNames)}</div>
          {text.body && <div className="text-xs text-gray-500 mt-0.5">{renderWithIcons(text.body, iconNames)}</div>}
          <div className="text-[10px] text-gray-300 mt-1">{formatDate(n.created_at, NOTICE_TIME)}</div>
        </button>
        <button onClick={() => dismiss(n)} disabled={leaving} aria-label={t.notifications.dismiss} title={t.notifications.dismiss}
          className="p-2 mt-1.5 mr-1 text-gray-300 hover:text-gray-600 shrink-0">
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  };

  return (
    // #572 — a flex wrapper, so the 36 px bell sits on the centre line of the
    // bar. An inline button in a block div sat on a text line, 2 px too high.
    <div ref={ref} className="relative flex">
      <button onClick={toggle} aria-label={t.notifications.title} aria-expanded={open} data-testid="notification-bell"
        className="btn-header">
        <BellIcon className="w-5 h-5" />
        {count > 0 && (
          <span data-testid="notification-count" className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-dropdown" className="fixed inset-x-0 top-14 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1 sm:w-80 bg-white rounded-b-xl sm:rounded-xl shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-sm">{t.notifications.title}</span>
            <div className="flex items-center gap-3">
              <button onClick={() => setUnreadOnly(u => !u)} className="text-[10px] text-gray-500 hover:underline">
                {unreadOnly ? t.notifications.showAll : t.notifications.unreadOnly}
              </button>
              {rows.some(n => !n.read) && (
                <button onClick={markAll} disabled={markAllBusy.busy} className="text-[10px] text-blue-600 hover:underline disabled:opacity-50">{t.notifications.markAllRead}</button>
              )}
            </div>
          </div>
          {visible.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">{t.notifications.empty}</div>
          ) : (
            entries.map(e => {
              if (e.kind === 'one') return row(e.notice);
              const expanded = openGroups.has(e.key);
              const unread = e.notices.some(n => !n.read);
              const title = (t.notifications.groups[e.type] ?? '{n}: {tournament}')
                .replace('{n}', String(e.notices.length)).replace('{tournament}', e.tournamentName);
              return (
                <div key={e.key}>
                  <button onClick={() => toggleGroup(e.key)} aria-expanded={expanded} data-testid="notification-group"
                    className={`w-full flex items-start gap-2 text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${unread ? '' : 'opacity-60'}`}>
                    <span aria-hidden="true" className="text-gray-400 mt-0.5">{expanded ? <ChevronDownIcon className="w-3.5 h-3.5" /> : <ChevronRightIcon className="w-3.5 h-3.5" />}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800">{title}</span>
                      <span className="block text-[10px] text-gray-300 mt-1">{formatDate(e.notices[0].created_at, NOTICE_TIME)}</span>
                    </span>
                  </button>
                  {expanded && e.notices.map(n => row(n, true))}
                </div>
              );
            })
          )}
          {push.supported && (
            <div className="px-4 py-2.5 border-t border-gray-100">
              <button disabled={push.loading} data-testid="push-toggle"
                onClick={() => (push.enabled ? push.disableWithResult().then(explainDisable) : push.enableWithResult().then(explainEnable))}
                className={`w-full text-left text-[11px] flex items-center gap-1.5 ${push.enabled ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'} transition-colors`}>
                <BellIcon className="w-3.5 h-3.5" filled={push.enabled} />
                {push.loading ? '...' : push.enabled ? t.notifications.pushEnabled : t.notifications.pushEnable}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
